import { useState, useEffect, useCallback } from "react";
import { useIsMobile } from "../hooks/useIsMobile";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";
const fmt = n => "$" + Math.round(n ?? 0).toLocaleString("es-CL");
const authFetch = url => fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

// ── Design system tokens ────────────────────────────────────────────────────
const DS = {
  green:  "#059669", greenBg:  "#ECFDF5", greenBorder:  "rgba(5,150,105,0.15)",
  red:    "#DC2626", redBg:    "#FEF2F2", redBorder:    "rgba(220,38,38,0.15)",
  amber:  "#D97706", amberBg:  "#FFFBEB", amberBorder:  "rgba(217,119,6,0.15)",
  indigo: "#4338CA", indigoBg: "#EEF2FF", indigoBorder: "rgba(67,56,202,0.15)",
  cyan:   "#0891B2", cyanBg:   "#ECFEFF",
  lbl:    "#9496B4",
  border: "#E4E6F5",
  shadow: "0 4px 20px rgba(67,56,202,0.08)",
  mono:   "'JetBrains Mono', monospace",
};

// ── Helpers ──────────────────────────────────────────────────────────────────
function periodoAnterior(desde, hasta) {
  const d = new Date(desde);
  const h = new Date(hasta);
  const dias = Math.round((h - d) / 86400000) + 1;
  const hAnterior = new Date(d); hAnterior.setDate(hAnterior.getDate() - 1);
  const dAnterior = new Date(hAnterior); dAnterior.setDate(dAnterior.getDate() - dias + 1);
  return {
    desde: dAnterior.toISOString().slice(0, 10),
    hasta: hAnterior.toISOString().slice(0, 10),
  };
}

function colorValor(n) { return n >= 0 ? DS.green : DS.red; }

// ── Flujo de caja ejecutivo ──────────────────────────────────────────────────
function FlujoCaja({ pasos }) {
  const isMobile = useIsMobile();
  return (
    <div style={{
      background: "#fff", border: `1px solid ${DS.border}`, borderRadius: 12,
      display: "flex",
      flexDirection: isMobile ? "column" : "row",
      alignItems: "stretch", overflow: "hidden",
    }}>
      {pasos.map((paso, i) => (
        <div key={paso.label} style={{ flex: 1, display: "flex", flexDirection: isMobile ? "row" : "column", alignItems: "stretch" }}>
          <div style={{
            flex: 1,
            padding: isMobile ? "14px 16px" : "18px 12px",
            textAlign: isMobile ? "left" : "center",
            borderLeft: !isMobile && i > 0 ? `1px solid ${DS.border}` : "none",
            borderTop: isMobile && i > 0 ? `1px solid ${DS.border}` : "none",
            display: "flex", flexDirection: "column",
            justifyContent: "center",
            gap: 2,
          }}>
            {/* Label + icon */}
            <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: isMobile ? "flex-start" : "center" }}>
              {paso.icon && <div style={{ fontSize: 14 }}>{paso.icon}</div>}
              <div style={{
                fontSize: 10, color: DS.lbl, textTransform: "uppercase",
                letterSpacing: "0.06em", fontWeight: 500,
              }}>
                {paso.label}
              </div>
            </div>
            {/* Valor */}
            <div style={{
              fontFamily: DS.mono, fontSize: isMobile ? 16 : 18, fontWeight: 700,
              color: paso.color || "#1E1E2E", letterSpacing: "-0.01em",
              textAlign: isMobile ? "left" : "center",
            }}>
              {paso.value}
            </div>
            {/* Fórmula / referencia */}
            {paso.sub && (
              <div style={{
                fontSize: 10, color: DS.lbl, lineHeight: 1.4,
                textAlign: isMobile ? "left" : "center",
                marginTop: 2,
              }}>
                {paso.sub}
              </div>
            )}
          </div>
          {!isMobile && i < pasos.length - 1 && (
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: 20, flexShrink: 0, color: "#CBD5E1", fontSize: 16,
              userSelect: "none",
            }}>
              →
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── KpiCard ──────────────────────────────────────────────────────────────────
function KpiCard({ label, value, sub, accent = DS.indigo, icon, tendencia }) {
  const [hovered, setHovered] = useState(false);
  const accentBg = accent + "18";

  let badge = null;
  if (tendencia != null) {
    const sube = tendencia > 0;
    badge = (
      <div style={{
        display: "inline-flex", alignItems: "center", gap: 3,
        fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20,
        background: sube ? DS.greenBg : DS.redBg,
        color: sube ? DS.green : DS.red,
        marginTop: 6,
      }}>
        {sube ? "↑" : "↓"} {Math.abs(tendencia).toFixed(1)}% vs período ant.
      </div>
    );
  } else if (tendencia === 0) {
    badge = (
      <div style={{ fontSize: 11, color: DS.lbl, marginTop: 6 }}>= sin cambio vs período ant.</div>
    );
  }

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "#fff", border: `1px solid ${DS.border}`, borderRadius: 12,
        borderTop: `3px solid ${accent}`,
        padding: "20px", flex: 1, minWidth: 160,
        boxShadow: hovered ? DS.shadow : "none",
        transition: "box-shadow 0.2s",
      }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
        <div style={{ fontSize: 11, color: DS.lbl, textTransform: "uppercase",
          letterSpacing: "0.06em", fontWeight: 500 }}>{label}</div>
        {icon && (
          <div style={{ width: 36, height: 36, borderRadius: 10, background: accentBg,
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>
            {icon}
          </div>
        )}
      </div>
      <div style={{ fontFamily: DS.mono, fontSize: 24, fontWeight: 700, color: "#1E1E2E", letterSpacing: "-0.02em" }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 12, color: DS.lbl, marginTop: 4 }}>{sub}</div>}
      {badge}
    </div>
  );
}

// ── Barra de estado ──────────────────────────────────────────────────────────
function BarraEstado({ d }) {
  const total = d.total_pedidos || 1;
  const segs = [
    { label: "Entregados",  v: d.total_entregados,  color: DS.green  },
    { label: "En tránsito", v: d.total_en_transito, color: DS.indigo },
    { label: "Pendientes",  v: d.total_pendientes,  color: DS.amber  },
    { label: "Cancelados",  v: d.total_cancelados,  color: "#94A3B8" },
    { label: "Devueltos",   v: d.total_fallidos,    color: DS.red    },
  ].filter(s => s.v > 0);

  return (
    <div>
      <div style={{ display: "flex", gap: 2, height: 10, marginBottom: "1.5rem" }}>
        {segs.map((s, idx) => {
          const w = (s.v / total) * 100;
          if (w < 0.5) return null;
          const first = idx === 0;
          const last  = idx === segs.length - 1;
          return (
            <div key={s.label} style={{
              flex: `${w} 0 0`, minWidth: 3,
              background: s.color,
              borderRadius: first ? "5px 2px 2px 5px" : last ? "2px 5px 5px 2px" : "2px",
              display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden",
            }}>
              {w >= 8 && (
                <span style={{ fontSize: 8, fontWeight: 700, color: "#fff",
                  fontFamily: DS.mono, textShadow: "0 1px 2px rgba(0,0,0,0.3)" }}>
                  {Math.round(w)}%
                </span>
              )}
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem 1.25rem" }}>
        {segs.map(s => (
          <div key={s.label} style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13 }}>
            <div style={{ width: 8, height: 8, borderRadius: 2, background: s.color }} />
            <span style={{ fontFamily: DS.mono, fontWeight: 600, color: "#1E1E2E" }}>{s.v}</span>
            <span style={{ color: DS.lbl }}>({Math.round((s.v / total) * 100)}%)</span>
            <span style={{ color: "#555" }}>{s.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Fila de desglose ─────────────────────────────────────────────────────────
function Fila({ label, formula, value, color, bold, refLabel, refValue }) {
  const isBold = !!bold;
  return (
    <div style={{ borderTop: isBold ? `1px solid ${DS.border}` : "none" }}>
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "flex-start",
        padding: isBold ? "10px 0 8px" : "7px 0", gap: 12,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, color: isBold ? "#1E1E2E" : "#555", fontWeight: isBold ? 600 : 400 }}>
            {label}
          </div>
          {formula && <div style={{ fontSize: 10, color: "#C7C9E4", marginTop: 1 }}>{formula}</div>}
        </div>
        <span style={{
          fontFamily: DS.mono, fontSize: 13, fontWeight: isBold ? 700 : 500,
          color: color || (isBold ? "#1E1E2E" : "#555"), whiteSpace: "nowrap",
        }}>{value}</span>
      </div>
      {refLabel && (
        <div style={{ display: "flex", justifyContent: "space-between", paddingLeft: 14,
          paddingBottom: 5, gap: 12 }}>
          <div style={{ fontSize: 11, color: DS.lbl, fontStyle: "italic" }}>{refLabel}</div>
          {refValue && <span style={{ fontFamily: DS.mono, fontSize: 11, color: DS.lbl, whiteSpace: "nowrap" }}>{refValue}</span>}
        </div>
      )}
    </div>
  );
}

// ── Total financiero destacado ────────────────────────────────────────────────
function TotalFin({ label, value, color = DS.indigo }) {
  return (
    <div style={{
      background: DS.indigoBg, borderRadius: 8, padding: "10px 14px",
      display: "flex", justifyContent: "space-between", alignItems: "center",
      margin: "4px 0",
    }}>
      <span style={{ fontSize: 13, color: DS.indigo, fontWeight: 600 }}>{label}</span>
      <span style={{ fontFamily: DS.mono, fontSize: 16, fontWeight: 700, color }}>{value}</span>
    </div>
  );
}

// ── Sección colapsable ────────────────────────────────────────────────────────
function SeccionColapsable({ label, formula, value, color, refLabel, refValue, children, defaultOpen = false }) {
  const [abierto, setAbierto] = useState(defaultOpen);
  return (
    <div style={{ borderTop: `1px solid ${DS.border}` }}>
      <div onClick={() => setAbierto(v => !v)}
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "10px 0", cursor: "pointer", gap: 12, userSelect: "none" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"
            style={{ flexShrink: 0, transition: "transform 0.2s",
              transform: abierto ? "rotate(90deg)" : "rotate(0deg)" }}>
            <path d="M4.5 2.5L9.5 7L4.5 11.5" stroke={DS.indigo} strokeWidth="1.5"
              strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#1E1E2E" }}>{label}</div>
            {formula && <div style={{ fontSize: 10, color: "#C7C9E4", marginTop: 1 }}>{formula}</div>}
          </div>
        </div>
        <span style={{ fontFamily: DS.mono, fontSize: 13, fontWeight: 700, color, whiteSpace: "nowrap" }}>{value}</span>
      </div>

      {refLabel && (
        <div style={{ display: "flex", justifyContent: "space-between", paddingLeft: 22,
          paddingBottom: 6, gap: 12 }}>
          <div style={{ fontSize: 11, color: DS.lbl, fontStyle: "italic" }}>{refLabel}</div>
          {refValue && <span style={{ fontFamily: DS.mono, fontSize: 11, color: DS.lbl, whiteSpace: "nowrap" }}>{refValue}</span>}
        </div>
      )}

      {abierto && (
        <div style={{ paddingLeft: 22, paddingBottom: 8 }}>
          {children}
        </div>
      )}
    </div>
  );
}

// ── Checkbox con monto USD editable ──────────────────────────────────────────
function CheckboxUsd({ label, usd, onChangeUsd, clp, checked, onToggle }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "5px 0" }}>
      <div onClick={onToggle} style={{
        width: 16, height: 16, borderRadius: 4, flexShrink: 0, cursor: "pointer",
        border: checked ? "none" : `1.5px solid ${DS.border}`,
        background: checked ? DS.indigo : "transparent",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        {checked && <div style={{ width: 8, height: 8, background: "#fff", borderRadius: 1,
          clipPath: "polygon(14% 44%,0 65%,50% 100%,100% 16%,80% 0%,43% 62%)" }} />}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, flex: 1, flexWrap: "wrap" }}>
        <span style={{ fontSize: 13, color: "#555" }}>{label} — USD</span>
        <input
          type="number" min={0} value={usd}
          onChange={e => onChangeUsd(Number(e.target.value) || 0)}
          onClick={e => e.stopPropagation()}
          style={{ width: 58, padding: "2px 6px", fontSize: 12, borderRadius: 6,
            border: `1px solid ${DS.border}`, outline: "none",
            fontFamily: DS.mono, color: "#1E1E2E", background: "#fff" }}
        />
        <span style={{ fontSize: 12, color: DS.lbl }}>/mes</span>
        <span style={{ fontFamily: DS.mono, fontSize: 11, color: DS.lbl }}>= {fmt(clp)}</span>
      </div>
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────
export default function Pulso({ productos, desde, hasta }) {
  const [datos,    setDatos]    = useState(null);
  const [datosAnt, setDatosAnt] = useState(null);
  const [cargando, setCargando] = useState(false);

  // Suscripciones
  const [shopify,       setShopify]       = useState(true);
  const [releasit,      setReleasit]      = useState(true);
  const [chateaProOn,   setChateaProOn]   = useState(true);
  const [shopifyUsd,    setShopifyUsd]    = useState(29);
  const [releasitUsd,   setReleasitUsd]   = useState(19);
  const [chateaProUsd,  setChateaProUsd]  = useState(49);
  const [usdRate,       setUsdRate]       = useState(950);

  const fetchDatos = useCallback(async (d, h) => {
    if (!d || !h) return;
    setCargando(true);
    try {
      const [res, resAnt] = await Promise.all([
        authFetch(`${API_URL}/api/pulso?desde=${d}&hasta=${h}`),
        (() => {
          const ant = periodoAnterior(d, h);
          return authFetch(`${API_URL}/api/pulso?desde=${ant.desde}&hasta=${ant.hasta}`);
        })(),
      ]);
      if (res.ok)    setDatos(await res.json());
      if (resAnt.ok) setDatosAnt(await resAnt.json());
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { fetchDatos(desde, hasta); }, [desde, hasta, fetchDatos]);

  if (cargando && !datos) return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, color: DS.lbl,
      fontSize: 13, padding: "3rem 0" }}>
      <div style={{ width: 18, height: 18, border: `2px solid ${DS.border}`,
        borderTop: `2px solid ${DS.indigo}`, borderRadius: "50%",
        animation: "spin 0.8s linear infinite" }} />
      Cargando...
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (!datos) return (
    <div style={{ color: DS.lbl, fontSize: 13, padding: "3rem 0" }}>Sin datos disponibles.</div>
  );

  const d  = datos    || {};
  const da = datosAnt || {};

  // ── Cálculos ────────────────────────────────────────────────────────────────
  const ventaTotal              = d.venta_total        ?? 0;
  const fleteEntregados         = d.flete_entregados   ?? 0;
  const costoProveedor          = d.costo_proveedor    ?? 0;
  const gastoMeta               = d.gasto_meta         ?? 0;
  const entradasPeriodo         = d.entradas_periodo   ?? 0;
  const salidasPeriodo          = d.salidas_periodo    ?? 0;
  const chateaProReal           = d.chateapro_real     ?? 0;
  const retirosRef              = d.retiros_ref        ?? 0;
  const fleteDevoluciones       = d.flete_devoluciones ?? 0;

  const margenProductosVendidos = ventaTotal - fleteEntregados - costoProveedor;
  const gastosAsociados         = gastoMeta + fleteDevoluciones;
  const margenReal              = margenProductosVendidos - gastosAsociados;
  const balanceBilletera        = entradasPeriodo - salidasPeriodo;
  const gananciaReal            = balanceBilletera - gastoMeta;

  // ChateaPro: si viene de planilla se usa ese valor; si no, se usa el editable en USD
  const chateaProUsaPlanilla = chateaProReal > 0;
  const chateaProCosto = chateaProUsaPlanilla
    ? (chateaProOn ? chateaProReal : 0)
    : (chateaProOn ? Math.round(chateaProUsd * usdRate) : 0);

  const costoShopify       = shopify  ? Math.round(shopifyUsd  * usdRate) : 0;
  const costoRelease       = releasit ? Math.round(releasitUsd * usdRate) : 0;
  const totalSuscripciones = chateaProCosto + costoShopify + costoRelease;
  const margenFinal        = margenReal - totalSuscripciones;

  const roasNum   = gastoMeta > 0 ? gananciaReal / gastoMeta : null;
  const roasLabel = roasNum != null ? roasNum.toFixed(2) : "—";

  // Tendencias
  const ventaAnt = da.venta_total        ?? 0;
  const mpvAnt   = (da.venta_total ?? 0) - (da.flete_entregados ?? 0) - (da.costo_proveedor ?? 0);
  const ganAnt   = ((da.entradas_periodo ?? 0) - (da.salidas_periodo ?? 0)) - (da.gasto_meta ?? 0);
  const mfAnt    = ganAnt - (da.chateapro_real ?? 0) - costoShopify - costoRelease;

  const tendVenta = ventaAnt !== 0 ? ((ventaTotal              - ventaAnt) / Math.abs(ventaAnt)) * 100 : null;
  const tendMpv   = mpvAnt   !== 0 ? ((margenProductosVendidos - mpvAnt)  / Math.abs(mpvAnt))   * 100 : null;
  const tendGan   = ganAnt   !== 0 ? ((gananciaReal            - ganAnt)  / Math.abs(ganAnt))   * 100 : null;
  const tendMf    = mfAnt    !== 0 ? ((margenFinal             - mfAnt)   / Math.abs(mfAnt))    * 100 : null;

  const card = (children, style = {}) => (
    <div style={{ background: "#fff", border: `1px solid ${DS.border}`, borderRadius: 12,
      padding: "20px", ...style }}>
      {children}
    </div>
  );

  const sectionLabel = (text) => (
    <div style={{ fontSize: 11, color: DS.lbl, textTransform: "uppercase",
      letterSpacing: "0.06em", fontWeight: 500, marginBottom: 16 }}>{text}</div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* Período */}
      {d.desde && (
        <div style={{ fontSize: 11, color: DS.lbl, display: "flex", alignItems: "center", gap: 8 }}>
          {cargando && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
              <div style={{ width: 10, height: 10, border: `1.5px solid ${DS.border}`,
                borderTop: `1.5px solid ${DS.indigo}`, borderRadius: "50%",
                animation: "spin 0.8s linear infinite" }} />
              Actualizando...
            </span>
          )}
          <span>{d.desde} → {d.hasta}</span>
          <span style={{ background: DS.indigoBg, color: DS.indigo, fontFamily: DS.mono,
            fontSize: 11, fontWeight: 600, padding: "1px 8px", borderRadius: 20 }}>
            {d.total_pedidos ?? 0} pedidos
          </span>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          RESUMEN
      ══════════════════════════════════════════════════════════════════════ */}
      {(
        <>
          {/* ── Indicadores generales (flujo de caja) ── */}
          <FlujoCaja pasos={[
            {
              label: "Venta total",
              icon: "💰",
              value: fmt(ventaTotal),
              sub: `Suma de pedidos entregados · ${d.total_entregados ?? 0} pedidos`,
              color: DS.indigo,
            },
            {
              label: "Margen de venta",
              icon: "📦",
              value: fmt(margenProductosVendidos),
              sub: "Venta − flete entregados − costo proveedor",
              color: colorValor(margenProductosVendidos),
            },
            {
              label: "Gastos asociados",
              icon: "📉",
              value: fmt(gastosAsociados),
              sub: `Meta Ads ${fmt(gastoMeta)} + fletes fallidos ${fmt(fleteDevoluciones)}`,
              color: DS.red,
            },
            {
              label: "Margen real",
              icon: "💵",
              value: fmt(margenReal),
              sub: "Margen de venta − gastos asociados",
              color: colorValor(margenReal),
            },
          ]} />

          {/* ── Estado de pedidos ── */}
          {card(
            <>
              {sectionLabel("Estado de pedidos en el período")}
              <BarraEstado d={d} />
            </>
          )}

          {/* ── Desglose financiero ── */}
          {card(
            <>
              {sectionLabel("Desglose financiero")}

              {/* Q de pedidos */}
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "0 0 14px", borderBottom: `1px solid ${DS.border}`, marginBottom: 14,
              }}>
                <div style={{ fontSize: 13, color: "#555" }}>📦 Pedidos entregados</div>
                <span style={{ fontFamily: DS.mono, fontSize: 16, fontWeight: 700, color: DS.indigo }}>
                  {d.total_entregados ?? 0}
                </span>
              </div>

              {/* Grupo 1: Venta → Margen de Venta */}
              <Fila label="Venta Total"
                formula="Suma TOTAL DE LA ORDEN · ESTATUS = ENTREGADO"
                value={fmt(ventaTotal)} color={DS.green} />
              <Fila label="− Costo Flete Entregado"
                formula="Suma PRECIO FLETE · ESTATUS = ENTREGADO"
                value={`−${fmt(fleteEntregados)}`} color={DS.red} />
              <Fila label="− Costo Producto Proveedor"
                formula="Suma PRECIO PROVEEDOR × CANTIDAD · ESTATUS = ENTREGADO"
                value={`−${fmt(costoProveedor)}`} color={DS.red} />
              <Fila label="= Margen de Venta"
                value={fmt(margenProductosVendidos)} color={colorValor(margenProductosVendidos)} bold />

              <div style={{ height: 14 }} />

              {/* Grupo 2: Gastos → Margen Real */}
              <Fila label="− Gasto Meta Ads"
                formula="Gasto Meta por fecha · Análisis Diario"
                value={`−${fmt(gastoMeta)}`} color={DS.red} />
              <Fila label="− Gasto Fletes Fallidos"
                formula="Suma PRECIO FLETE · ESTATUS = DEVUELTO"
                value={`−${fmt(fleteDevoluciones)}`} color={DS.red} />
              <Fila label="= Margen Real"
                value={fmt(margenReal)} color={colorValor(margenReal)} bold />

              <div style={{ height: 14 }} />

              {/* Suscripciones */}
              <div style={{ background: "#FAFBFF", border: `1px solid ${DS.border}`, borderRadius: 10,
                padding: "14px 16px" }}>
                <div style={{ fontSize: 11, color: DS.lbl, textTransform: "uppercase",
                  letterSpacing: "0.06em", fontWeight: 500, marginBottom: 12 }}>
                  Suscripciones y herramientas
                </div>

                {/* TRM */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                  <span style={{ fontSize: 12, color: DS.lbl }}>TRM CLP/USD</span>
                  <input type="number" value={usdRate} onChange={e => setUsdRate(Number(e.target.value) || 0)}
                    style={{ width: 80, padding: "4px 8px", fontSize: 12, borderRadius: 6,
                      border: `1px solid ${DS.border}`, outline: "none",
                      fontFamily: DS.mono, color: "#1E1E2E", background: "#fff" }} />
                </div>

                {/* ChateaPro */}
                <div style={{ borderBottom: `1px solid ${DS.border}`, paddingBottom: 8, marginBottom: 4 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, paddingTop: 4 }}>
                    <div onClick={() => setChateaProOn(v => !v)} style={{
                      width: 16, height: 16, borderRadius: 4, flexShrink: 0, cursor: "pointer",
                      border: chateaProOn ? "none" : `1.5px solid ${DS.border}`,
                      background: chateaProOn ? DS.indigo : "transparent",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      {chateaProOn && <div style={{ width: 8, height: 8, background: "#fff", borderRadius: 1,
                        clipPath: "polygon(14% 44%,0 65%,50% 100%,100% 16%,80% 0%,43% 62%)" }} />}
                    </div>

                    {chateaProUsaPlanilla ? (
                      /* Valor real desde planilla */
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ fontSize: 13, color: "#555" }}>ChateaPro</span>
                          <span style={{ fontFamily: DS.mono, fontSize: 13, fontWeight: 500, color: DS.red }}>
                            −{fmt(chateaProReal)}
                          </span>
                        </div>
                        <div style={{
                          fontSize: 10, color: DS.green, marginTop: 3,
                          background: DS.greenBg, border: `1px solid ${DS.greenBorder}`,
                          borderRadius: 6, padding: "3px 8px", display: "inline-block",
                        }}>
                          ✓ Valor tomado de planilla Dropi (TRANSFERENCIA DE WALLET) — se usa el monto real en lugar de los USD 49 fijos
                        </div>
                      </div>
                    ) : (
                      /* Input editable USD 49 fijo */
                      <div style={{ display: "flex", alignItems: "center", gap: 6, flex: 1, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 13, color: "#555" }}>ChateaPro — USD</span>
                        <input
                          type="number" min={0} value={chateaProUsd}
                          onChange={e => setChateaProUsd(Number(e.target.value) || 0)}
                          style={{ width: 58, padding: "2px 6px", fontSize: 12, borderRadius: 6,
                            border: `1px solid ${DS.border}`, outline: "none",
                            fontFamily: DS.mono, color: "#1E1E2E", background: "#fff" }}
                        />
                        <span style={{ fontSize: 12, color: DS.lbl }}>/mes</span>
                        <span style={{ fontFamily: DS.mono, fontSize: 11, color: DS.lbl }}>
                          = {fmt(Math.round(chateaProUsd * usdRate))}
                        </span>
                        <span style={{ fontSize: 10, color: DS.lbl }}>
                          (no encontrado en planilla — usando valor manual)
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <CheckboxUsd
                  label="Shopify" usd={shopifyUsd} onChangeUsd={setShopifyUsd}
                  clp={costoShopify} checked={shopify} onToggle={() => setShopify(v => !v)} />
                <CheckboxUsd
                  label="Releasit" usd={releasitUsd} onChangeUsd={setReleasitUsd}
                  clp={costoRelease} checked={releasit} onToggle={() => setReleasit(v => !v)} />

                <div style={{ borderTop: `1px solid ${DS.border}`, marginTop: 8, paddingTop: 8,
                  display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 12, color: DS.lbl }}>Total suscripciones</span>
                  <span style={{ fontFamily: DS.mono, fontSize: 13, fontWeight: 700, color: DS.red }}>
                    −{fmt(totalSuscripciones)}
                  </span>
                </div>
              </div>

              <div style={{ height: 8 }} />
              <TotalFin label="= Margen Real descontando suscripciones" value={fmt(margenFinal)}
                color={margenFinal >= 0 ? DS.green : DS.red} />
            </>
          )}
        </>
      )}

    </div>
  );
}
